import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');
    if (newPrice && currentPrice) currentPrice.replaceWith(newPrice);

    // Update the savings pill + its per-selling-plan data island. Both are
    // siblings of the price container (not inside it), so replacing the price
    // container alone would leave the previous variant's savings on screen.
    // Swapping the fresh server-rendered nodes keeps the pill in sync with the
    // selected variant (and re-seeds the island the subscription sync reads).
    this.#replaceSibling(newProductPrice, '.price-savings', '[ref="priceContainer"]');
    this.#replaceSibling(newProductPrice, 'script[data-price-savings-plans]', '.price-savings');

    // Update volume pricing note
    const currentNote = this.querySelector('.volume-pricing-note');
    const newNote = newProductPrice.querySelector('.volume-pricing-note');

    if (!newNote) {
      currentNote?.remove();
    } else if (!currentNote) {
      this.querySelector('[ref="priceContainer"]')?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      currentNote.replaceWith(newNote);
    }
  };

  /**
   * Sync a node that lives beside the price container (savings pill, data island)
   * from the freshly rendered product-price: replace it, remove it if the new
   * variant no longer has one, or insert it if it newly appears.
   * @param {Element} source - The new product-price element from the fetched HTML.
   * @param {string} selector - The node to sync.
   * @param {string} afterSelector - Anchor to insert after when newly appearing.
   */
  #replaceSibling(source, selector, afterSelector) {
    const incoming = source.querySelector(selector);
    const existing = this.querySelector(selector);
    if (existing && incoming) {
      existing.replaceWith(incoming);
    } else if (existing && !incoming) {
      existing.remove();
    } else if (!existing && incoming) {
      this.querySelector(afterSelector)?.insertAdjacentElement('afterend', incoming);
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
